/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable no-useless-catch */
/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable consistent-return */
import * as Sentry from '@sentry/react';
import Keycloak from 'keycloak-js';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { useStoreActions } from './reducers';
import { User } from './types';
import { registerAuthTokenRefresher } from './utils/requests';

// TODO (mw) silenced an error here that keycloak context changes every render. revisit
interface KeycloakContextProps {
  keycloak: Keycloak | null;
  refreshKeycloakToken: () => Promise<void>;
}

const KeycloakContext = createContext<KeycloakContextProps>({
  keycloak: null,
  refreshKeycloakToken: async () => {},
});

const keycloakConfig = {
  // Your Keycloak configuration
  realm: `${process.env.REACT_APP_KEYCLOAK_REALM}`,
  url: `${process.env.REACT_APP_KEYCLOAK_BASE_URL}`,
  clientId: `${process.env.REACT_APP_KEYCLOAK_CLIENT_ID}`,
  scope: 'openid',
};

export const PREFERRED_IDP_STORAGE_KEY = 'preferred-idp';

const SUPPORTED_REAUTH_IDPS = new Set(['google', 'saml-ouster']);

export function getPreferredIdpHint(): string | undefined {
  const idp = localStorage.getItem(PREFERRED_IDP_STORAGE_KEY);
  return idp && SUPPORTED_REAUTH_IDPS.has(idp) ? idp : undefined;
}

function rememberPreferredIdp(keycloak: Keycloak) {
  if (!keycloak.authenticated) return;

  const idp = keycloak.idTokenParsed?.idp;
  if (typeof idp === 'string' && SUPPORTED_REAUTH_IDPS.has(idp)) {
    localStorage.setItem(PREFERRED_IDP_STORAGE_KEY, idp);
  } else {
    localStorage.removeItem(PREFERRED_IDP_STORAGE_KEY);
  }
}

function getUserInfo(keycloak: Keycloak): User | undefined {
  if (keycloak.authenticated && keycloak.idTokenParsed && keycloak.token) {
    const userInfo: User = {
      name: keycloak.idTokenParsed.name,
      email: keycloak.idTokenParsed.email,
      accessToken: keycloak.token,
      identity_id: keycloak.idTokenParsed.sub || '',
    };
    return userInfo;
  }
}

export const KeycloakProvider: React.FC<{
  children?: React.ReactNode; // Explicitly define children here
}> = ({ children }) => {
  const { setUser, setIsAuthenticated } = useStoreActions((actions) => ({
    ...actions.user,
  }));
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const location = useLocation();
  const isReauthenticatingRef = useRef(false);

  const reauthenticateWithPreferredIdp = useCallback(
    async (instance: Keycloak) => {
      if (isReauthenticatingRef.current) return;

      isReauthenticatingRef.current = true;
      try {
        const idpHint = getPreferredIdpHint();
        await instance.login({
          ...(idpHint ? { idpHint } : {}),
          redirectUri: window.location.href,
        });
      } catch (error) {
        isReauthenticatingRef.current = false;
        console.error('Failed to start reauthentication.', error);
        await instance.logout({
          redirectUri: `${window.location.origin}/login`,
        });
      }
    },
    []
  );

  const initKeycloak = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const keycloak = new Keycloak(keycloakConfig);
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    keycloak.onTokenExpired = async () => {
      // if user is offline, the app will not refresh token
      // console.log('navigator.onLine: ', navigator.onLine);
      if (!navigator.onLine) return;

      try {
        await keycloak.updateToken(30);
        rememberPreferredIdp(keycloak);
        const userInfo = getUserInfo(keycloak);
        if (userInfo) {
          setUser(userInfo);
        }
      } catch {
        console.error(
          'Keycloak refresh failed while user is online, reauthenticate with Google.'
        );
        await reauthenticateWithPreferredIdp(keycloak);
      }
    };

    keycloak.onAuthLogout = () => {
      /* 
        When user on login page, we don't want to redirect to login page again cause the lost of error message
      */
      if (location.pathname !== '/login' && location.pathname !== '/register') {
        void reauthenticateWithPreferredIdp(keycloak);
      }
    };

    const initOptions: Keycloak.KeycloakInitOptions = {
      enableLogging: false,
      messageReceiveTimeout: 100000,
      pkceMethod: 'S256',
      checkLoginIframe: false,
    };

    try {
      await keycloak.init(initOptions);
    } catch {
      console.error('Error initializing keycloak ');
    }

    setKeycloak(keycloak);
  }, [location.pathname, reauthenticateWithPreferredIdp, setUser]);

  useEffect(() => {
    // We run this if keycloak was not initialized
    if (!keycloak) {
      // Initializing the Keycloak SDK is inherently async (network calls
      // inside keycloak.init()) and its result is only committed via
      // setKeycloak() once that promise resolves - there's no way to derive
      // this during render, so this is a legitimate one-time async trigger.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      initKeycloak();
    }
  }, [keycloak, initKeycloak, location]);

  useEffect(() => {
    // Additional actions to perform after Keycloak initialization
    if (keycloak) {
      rememberPreferredIdp(keycloak);
      const userInfo = getUserInfo(keycloak);
      if (userInfo) {
        setIsAuthenticated(true);
        setUser(userInfo);
      }
    }
  }, [keycloak, setUser, setIsAuthenticated]);

  const hasReportedRefreshFailureRef = useRef(false);

  const refreshKeycloakToken = useCallback(async () => {
    if (!navigator.onLine) return;
    if (keycloak) {
      try {
        await keycloak.updateToken(30);
        hasReportedRefreshFailureRef.current = false;
        rememberPreferredIdp(keycloak);
        const userInfo = getUserInfo(keycloak);
        if (userInfo) {
          setUser(userInfo);
        }
      } catch (err) {
        console.error(
          'Keycloak auto refresh failed when window gain focus but token expired.'
        );
        // This can fire on every window-focus event while the token is
        // expired - report only the first failure so a slow logout
        // redirect doesn't produce a burst of duplicate events.
        if (!hasReportedRefreshFailureRef.current) {
          hasReportedRefreshFailureRef.current = true;
          // Routine session expiry (backgrounded tab, refresh token expired
          // server-side) - expected, not an alertable error. Reported at
          // 'warning' so a spike is still visible if the refresh mechanism
          // itself breaks, without paging on every normal logout.
          Sentry.captureException(err, {
            level: 'warning',
            tags: {
              section: 'KeycloakContext',
              thunk: 'refreshKeycloakToken',
            },
          });
        }
        await reauthenticateWithPreferredIdp(keycloak);
      }
    }
  }, [keycloak, reauthenticateWithPreferredIdp, setUser]);

  useEffect(() => {
    registerAuthTokenRefresher(refreshKeycloakToken);

    return () => {
      registerAuthTokenRefresher(null);
    };
  }, [refreshKeycloakToken]);

  return (
    <KeycloakContext.Provider
      value={{
        keycloak,
        refreshKeycloakToken,
      }}
    >
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = (): KeycloakContextProps =>
  useContext(KeycloakContext);
